import React, { useState, useEffect, useRef, useCallback, useMemo, useContext } from 'react';
import { useConversation } from '../../contexts/ConversationContext';
import { CouncilContext } from '../../contexts/CouncilContext';
import { useCouncilProposals } from '../../hooks/useCouncilProposals';
import { diag, useDiagMount, useDiagWatch } from '../../utils/trailhead_diag';
import { CrewStrip } from './CrewStrip';
import { HillChip } from './HillChip';
import { LensPill } from './LensPill';
import { ProposalReviewDialog } from './ProposalReviewDialog';
import { ProposalsChip } from './ProposalsChip';
import { SessionSpendHud } from './SessionSpendHud';
import { useExpeditionLog } from '../../stores/expeditionLog';

interface TrailheadHeaderProps {
  sessionId?: string;
}

const TRAIL_NAME_KEY_PREFIX = 'trailhead.trail-name.';

const trailNameKey = (sessionId?: string): string =>
  `${TRAIL_NAME_KEY_PREFIX}${sessionId || 'no-session'}`;

const readUserName = (sessionId?: string): string => {
  try {
    return localStorage.getItem(trailNameKey(sessionId)) || '';
  } catch {
    return '';
  }
};

const writeUserName = (sessionId: string | undefined, name: string): void => {
  try {
    localStorage.setItem(trailNameKey(sessionId), name);
  } catch {}
};

const removeUserName = (sessionId: string | undefined): void => {
  try {
    localStorage.removeItem(trailNameKey(sessionId));
  } catch {}
};

const formatRelative = (iso: string | undefined, nowMs: number): string => {
  if (!iso) return '';
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return '';
  const delta = Math.max(0, nowMs - then);
  const sec = Math.floor(delta / 1000);
  if (sec < 30) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
};

/**
 * Header drops trail-name as primary identity. Renders persona-based
 * default ("Sage · 4 messages · 2m ago") with always-visible ✎ at low
 * opacity for opt-in user naming. No auto-name from first message.
 */
export const TrailheadHeader: React.FC<TrailheadHeaderProps> = ({ sessionId }) => {
  const { state } = useConversation();

  const personaName =
    state.participants?.find((p: any) => p.type === 'persona')?.name || 'this trail';

  const [userName, setUserName] = useState<string>(() => readUserName(sessionId));
  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState<string>(userName);
  const inputRef = useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = useState(false);

  useDiagMount('header', 'TrailheadHeader', { sessionId, personaName });
  useDiagWatch('header', 'name source', {
    kind: userName ? 'user' : 'default',
    userName: userName || null,
    personaName,
    sessionId: sessionId ?? null,
  });
  useDiagWatch('header', 'editing', { editing });
  useDiagWatch('header', 'live stats', {
    messages: state.messages.length,
    lastTs: state.messages[state.messages.length - 1]?.timestamp ?? null,
  });

  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setUserName(readUserName(sessionId));
  }, [sessionId]);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    setDraftName(userName);
    setEditing(true);
  }, [userName]);

  const commitEdit = useCallback(() => {
    const trimmed = draftName.trim();
    if (trimmed) {
      setUserName(trimmed);
      writeUserName(sessionId, trimmed);
      diag({ surface: 'header', event: 'commit · localStorage write', detail: { sessionId, value: trimmed } });
    } else {
      setUserName('');
      removeUserName(sessionId);
      diag({ surface: 'header', event: 'commit · localStorage remove', detail: { sessionId } });
    }
    setEditing(false);
  }, [draftName, sessionId]);

  const cancelEdit = useCallback(() => {
    setDraftName(userName);
    setEditing(false);
  }, [userName]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelEdit();
      }
    },
    [commitEdit, cancelEdit]
  );

  const messageCount = state.messages.length;
  const lastMessage = state.messages[state.messages.length - 1];
  const lastActivity = useMemo(
    () => formatRelative(lastMessage?.timestamp, nowMs),
    [lastMessage?.timestamp, nowMs]
  );

  // Council Trail Task 6.1 — read CouncilContext optionally. No provider =
  // Solo Trail mode (existing behavior). roster.length > 0 = council mode
  // (two-row chrome with CrewStrip + LensPill + HillChip).
  const councilCtx = useContext(CouncilContext);
  const roster = councilCtx?.state?.roster ?? [];
  const councilMode = roster.length > 0;

  // G4b — turn_message_id → persona name, so the spend HUD's breach list shows a name,
  // not a raw UUID. Degrades to a short-id label when a name can't resolve (e.g. merge turns).
  const breachLabels = useMemo(() => {
    const m = new Map<string, string>();
    const byId = (councilCtx?.state as any)?.personasById ?? {};
    for (const msg of (state.messages ?? []) as any[]) {
      const pid = msg?.metadata?.persona_id;
      const name = pid ? byId[pid]?.name : undefined;
      if (name) m.set(msg.id, name);
    }
    return m;
  }, [state.messages, councilCtx?.state]);
  const operatorDisplayName =
    state.participants?.find((p: any) => p.type === 'user')?.name || 'You';

  // Council Trail v0.1 Item 8 — proposals chip. Hook is safe to call
  // outside a council session (just returns []), but we only surface
  // the chip in councilMode to keep solo-trail chrome quiet.
  const { proposals, resolve } = useCouncilProposals(sessionId || '');
  const [proposalsDialogOpen, setProposalsDialogOpen] = useState(false);

  // G3a — Expedition Log toggle + unread badge (hook at the component top per
  // react-hooks/rules-of-hooks; lint runs --max-warnings 0).
  const { open: logOpen, setOpen: setLogOpen, logState: expLogState } = useExpeditionLog();

  // Auto-pop the review dialog when a new proposal arrives AND the
  // operator is focused on the page (document.hasFocus()). Skips
  // background-tab pops (annoying). Skips when count drops (let
  // auto-close in the dialog handle that). Initial hydrate at mount
  // does NOT pop — only count GROWTH triggers it.
  const prevProposalCountRef = useRef<number>(0);
  const isInitialMountRef = useRef(true);
  useEffect(() => {
    const prev = prevProposalCountRef.current;
    const curr = proposals.length;
    prevProposalCountRef.current = curr;
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }
    if (curr > prev) {
      try {
        if (document.hasFocus()) setProposalsDialogOpen(true);
      } catch {
        // hasFocus unavailable (test env or older browser) — skip pop.
      }
    }
  }, [proposals.length]);

  return (
    <div
      data-testid="trailhead-header"
      className={`border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 transition-opacity duration-500 ${
        mounted ? 'opacity-100' : 'opacity-0'
      }`}
    >
      <div
        data-testid="trailhead-row-1"
        className="flex items-center justify-between px-4 py-2 text-sm gap-4 group min-w-0"
      >
        <div className="flex items-center gap-2 text-gray-800 dark:text-gray-100 min-w-0 flex-1">
          {editing ? (
            <>
              <input
                ref={inputRef}
                data-testid="trailhead-trail-name-input"
                type="text"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onBlur={commitEdit}
                onKeyDown={onKeyDown}
                maxLength={80}
                placeholder={`Name this trail (or leave blank for ${personaName})`}
                className="font-medium bg-white dark:bg-gray-800 border border-amber-400 dark:border-amber-500 rounded px-1.5 py-0.5 outline-none focus:border-amber-500 text-gray-800 dark:text-gray-100 min-w-0 flex-1 max-w-md"
                aria-label="Trail name"
              />
              <span className="text-gray-400 dark:text-gray-500">·</span>
              <span className="font-medium truncate text-gray-500 dark:text-gray-400">{personaName}</span>
            </>
          ) : (
            <>
              {userName && (
                <span className="font-medium truncate">{userName}</span>
              )}
              {userName && <span className="text-gray-400 dark:text-gray-500">·</span>}
              <span className="font-medium truncate">{personaName}</span>
              <button
                type="button"
                data-testid="trailhead-edit-affordance"
                onClick={startEdit}
                className="text-gray-300 dark:text-gray-600 group-hover:text-amber-500 dark:group-hover:text-amber-400 text-xs transition-colors shrink-0 px-0.5"
                aria-label={userName ? 'Rename trail' : 'Name this trail'}
                title={userName ? 'Rename trail' : 'Name this trail'}
              >
                ✎
              </button>
            </>
          )}
        </div>
        {councilMode && (
          <CrewStrip
            roster={roster}
            operatorMarker={{ displayName: operatorDisplayName }}
            fadeInOnMount={true}
          />
        )}
        {!councilMode && messageCount > 0 && (
          <div
            data-testid="trailhead-live-stats"
            className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5 shrink-0"
          >
            <span className="tabular-nums">
              {messageCount} {messageCount === 1 ? 'message' : 'messages'}
            </span>
            {lastActivity && (
              <>
                <span className="text-gray-300 dark:text-gray-600">·</span>
                <span className="tabular-nums">{lastActivity}</span>
              </>
            )}
          </div>
        )}
      </div>
      {/* Show row-2 chrome when the session is in council mode (peers
          on roster) OR when there are pending proposals — the latter
          case catches lab-inject flows where backend auto-seeded a peer
          but the roster-refresh WS event (peer_invited) didn't reach
          CouncilContext before the proposal_queued event tried to
          surface the chip. Without this dual-gate, pending proposals
          stay invisible until F5 hydrates the roster. (2026-05-17) */}
      {(councilMode || proposals.length > 0) && (
        <div
          data-testid="trailhead-row-2"
          className="flex items-center px-4 pb-2 text-sm gap-3 min-w-0"
        >
          <LensPill />
          <HillChip sessionId={sessionId} />
          <ProposalsChip
            pendingCount={proposals.length}
            onClick={() => setProposalsDialogOpen(true)}
          />
          <SessionSpendHud cost={(councilCtx?.state as any)?.cost} labelByTurn={breachLabels} />
          {/* G3a — Expedition Log drawer toggle + unread badge */}
          <button type="button" data-testid="expedition-log-toggle"
            onClick={() => setLogOpen(!logOpen)}
            className="inline-flex items-center gap-1 text-[11px] opacity-80 hover:opacity-100">
            <span aria-hidden>📜</span><span>Expedition Log</span>
            {expLogState.unread > 0 && (
              <span data-testid="exp-log-unread"
                className="ml-0.5 px-1 rounded-full bg-blue-600 text-white text-[10px]">
                {expLogState.unread}
              </span>)}
            <span aria-hidden>{logOpen ? '▾' : '▸'}</span>
          </button>
          {/* CostMeter + ModeChip — v0.1+ chrome */}
        </div>
      )}
      {(councilMode || proposals.length > 0) && (
        <ProposalReviewDialog
          open={proposalsDialogOpen}
          onClose={() => setProposalsDialogOpen(false)}
          proposals={proposals}
          resolve={resolve}
        />
      )}
    </div>
  );
};
